/** Small data hooks: `useQuery` (fetch + cache + refresh), `useDebounced`, `useAsyncAction`. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api.ts';

const cache = new Map<string, { at: number; value: unknown }>();
export function invalidate(prefix = ''): void { for (const k of [...cache.keys()]) if (k.startsWith(prefix)) cache.delete(k); }

export interface QueryState<T> { data: T | null; error: ApiError | null; loading: boolean; refresh: () => Promise<void> }

/** Runs `fn` when `key` changes; cached results show instantly while a fresh copy loads. Pass key=null to pause. */
export function useQuery<T>(key: string | null, fn: () => Promise<T>, opts: { ttlMs?: number; keepPrevious?: boolean } = {}): QueryState<T> {
  const cached = key ? (cache.get(key) as { at: number; value: T } | undefined) : undefined;
  const [data, setData] = useState<T | null>(cached?.value ?? null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(!!key && !cached);
  const fnRef = useRef(fn); fnRef.current = fn;
  const seq = useRef(0);
  const load = useCallback(async () => {
    if (!key) return;
    const my = ++seq.current;
    setLoading(true);
    try {
      const value = await fnRef.current();
      if (my !== seq.current) return;
      cache.set(key, { at: Date.now(), value });
      setData(value); setError(null);
    } catch (e) {
      if (my !== seq.current) return;
      setError(e instanceof ApiError ? e : new ApiError(0, 'unknown', (e as Error).message));
    } finally { if (my === seq.current) setLoading(false); }
  }, [key]);
  useEffect(() => {
    if (!key) { setData(null); setLoading(false); return; }
    const c = cache.get(key) as { at: number; value: T } | undefined;
    if (c) { setData(c.value); if (Date.now() - c.at < (opts.ttlMs ?? 15_000)) { setLoading(false); return; } }
    else if (!opts.keepPrevious) setData(null);
    void load();
  }, [key, load, opts.ttlMs, opts.keepPrevious]);
  return { data, error, loading, refresh: load };
}

export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

/** Wraps an async handler with busy + error state for buttons and forms. */
export function useAsyncAction<A extends unknown[]>(fn: (...args: A) => Promise<void>): { run: (...args: A) => Promise<void>; busy: boolean; error: ApiError | null; clear: () => void } {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const run = useCallback(async (...args: A) => {
    setBusy(true); setError(null);
    try { await fn(...args); }
    catch (e) { setError(e instanceof ApiError ? e : new ApiError(0, 'unknown', (e as Error).message)); throw e; }
    finally { setBusy(false); }
  }, [fn]);
  return { run, busy, error, clear: () => setError(null) };
}
